import {
	type ClientPortfolioBankProductToolRequestDTO,
	type ClientPortfolioBondInstructionRequestDTO,
	type ClientPortfolioCommoditiesToolRequestDTO,
	type ClientPortfolioCryptocurrenciesToolRequestDTO,
	type ClientPortfolioDetailBondResponseDTO,
	type ClientPortfolioDetailCommodityToolResponseDTO,
	type ClientPortfolioDetailFundToolResponseDTO,
	type ClientPortfolioDetailToolsResponseDTO,
	type ClientPortfolioInvestmentBondToolRequestDTO,
	type ClientPortfolioInvestmentEnterFeeRequestDTO,
	type ClientPortfolioInvestmentEnterFeeResponseDTO,
	type ClientPortfolioInvestmentFundToolRequestDTO,
	type ClientPortfolioManualValueResponseDTO,
	type ClientPortfolioToolsRequestDTO,
	ClientPortfolioToolTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type {
	InvestmentInstructionOutput,
	InvestmentToolOutput,
} from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolSchema";
import { getInvestmentToolKind } from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolType";
import { mapCryptoInstructionToRequest } from "web_myplann/Portfolio/Forms/ToolsShared/mapCryptoInstruction";
import { mapInstructionToRequest } from "web_myplann/Portfolio/Forms/ToolsShared/mapInstruction";
import { findOriginal } from "web_myplann/Portfolio/Forms/ToolsShared/toolRequestHelpers";
import { ToolKind } from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { mapToolManualValuesToRequest } from "web_myplann/Portfolio/PortfolioShared/ManualValue/mapToolManualValuesToRequest";

type ToolsRequest = {
	[K in keyof ClientPortfolioToolsRequestDTO]-?: NonNullable<
		ClientPortfolioToolsRequestDTO[K]
	>;
};

type OriginalTools = ClientPortfolioDetailToolsResponseDTO | null | undefined;

type RequestContext = {
	original: OriginalTools;
	manualValuesById: Map<number, ClientPortfolioManualValueResponseDTO[]>;
	originalFundInstructions:
		| ClientPortfolioDetailFundToolResponseDTO["instructions"]
		| undefined;
	originalCommodityInstructions:
		| ClientPortfolioDetailCommodityToolResponseDTO["commodities"]
		| undefined;
};

const mapEnterFeeToRequest = (
	enterFee: ClientPortfolioInvestmentEnterFeeResponseDTO | null | undefined,
): ClientPortfolioInvestmentEnterFeeRequestDTO | null => {
	if (!enterFee) {
		return null;
	}
	const { id: _id, ...request } = enterFee;
	return request;
};

const mapBondInstructionToRequest = (
	instruction: ClientPortfolioDetailBondResponseDTO,
): ClientPortfolioBondInstructionRequestDTO => ({
	id: instruction.id,
	issueDate: instruction.issueDate,
	maturityDate: instruction.maturityDate,
	price: instruction.price,
	currency: instruction.currency,
	count: instruction.count,
	yield: instruction.yield,
	tax: instruction.tax,
	fee: instruction.fee,
	interestType: instruction.interestType,
	frequency: instruction.frequency,
	rates: instruction.rates,
});

const isToolOfKind = (kind: ToolKind) => (tool: InvestmentToolOutput) =>
	getInvestmentToolKind(tool) === kind;

const getToolManualValues = (
	tool: InvestmentToolOutput,
	{ manualValuesById }: RequestContext,
) => {
	const manualValues = mapToolManualValuesToRequest(
		tool,
		(tool.id === null ? undefined : manualValuesById.get(tool.id)) ?? [],
	);
	return manualValues === undefined ? {} : { manualValues };
};

const mapFundInstructionToRequest = (
	instruction: InvestmentInstructionOutput,
	{ originalFundInstructions }: RequestContext,
) => {
	const advisor = findOriginal(originalFundInstructions, instruction.id);
	return {
		...mapInstructionToRequest(instruction),
		transactionType: advisor?.transactionType ?? null,
		enterFee: mapEnterFeeToRequest(advisor?.enterFee),
	};
};

const mapCommodityInstructionToRequest = (
	instruction: InvestmentInstructionOutput,
	{ originalCommodityInstructions }: RequestContext,
) => {
	const advisor = findOriginal(originalCommodityInstructions, instruction.id);
	return {
		...mapInstructionToRequest(instruction),
		spread: advisor?.spread ?? null,
		enterFee: mapEnterFeeToRequest(advisor?.enterFee),
	};
};

const mapFundToRequest = (
	tool: InvestmentToolOutput,
	context: RequestContext,
): ClientPortfolioInvestmentFundToolRequestDTO => ({
	id: tool.id,
	type: tool.type,
	identificationType: tool.identificationType,
	identification: tool.identification,
	name: tool.name,
	...getToolManualValues(tool, context),
	instructions: tool.instructions.map((instruction) =>
		mapFundInstructionToRequest(instruction, context),
	),
});

const mapBondToRequest = (
	tool: InvestmentToolOutput,
	context: RequestContext,
): ClientPortfolioInvestmentBondToolRequestDTO => ({
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Bond,
	identificationType: tool.identificationType,
	identification: tool.identification,
	name: tool.name,
	...getToolManualValues(tool, context),
	instructions: (
		findOriginal(context.original?.bonds, tool.id)?.instructions ?? []
	).map(mapBondInstructionToRequest),
});

const mapCommodityToRequest = (
	tool: InvestmentToolOutput,
	context: RequestContext,
): ClientPortfolioCommoditiesToolRequestDTO => ({
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Commodity,
	identification: tool.identification,
	name: tool.name,
	...getToolManualValues(tool, context),
	commodities: tool.instructions.map((instruction) =>
		mapCommodityInstructionToRequest(instruction, context),
	),
});

const mapCryptoToRequest = (
	tool: InvestmentToolOutput,
	context: RequestContext,
): ClientPortfolioCryptocurrenciesToolRequestDTO => ({
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Crypto,
	identification: tool.identification,
	name: tool.name,
	holdingType: tool.holdingType,
	...getToolManualValues(tool, context),
	cryptocurrencyInstructions: tool.instructions.map((instruction) =>
		mapCryptoInstructionToRequest(instruction, tool.identification ?? ""),
	),
});

const mapBankProductToRequest = (
	tool: InvestmentToolOutput,
	context: RequestContext,
): ClientPortfolioBankProductToolRequestDTO => ({
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.BankProduct,
	name: tool.name,
	...getToolManualValues(tool, context),
	instructions: tool.instructions.map(mapInstructionToRequest),
});

export const mapInvestmentToolsToRequest = (
	tools: InvestmentToolOutput[],
	original?: OriginalTools,
): ToolsRequest => {
	const context: RequestContext = {
		original,
		manualValuesById: new Map(
			[
				...(original?.funds ?? []),
				...(original?.bonds ?? []),
				...(original?.commodities ?? []),
				...(original?.cryptocurrencies ?? []),
				...(original?.bankProducts ?? []),
			].map((tool) => [tool.id, tool.manualValues]),
		),
		originalFundInstructions: original?.funds.flatMap(
			(tool) => tool.instructions,
		),
		originalCommodityInstructions: original?.commodities.flatMap(
			(tool) => tool.commodities,
		),
	};

	return {
		funds: tools
			.filter(isToolOfKind(ToolKind.Investment))
			.map((tool) => mapFundToRequest(tool, context)),
		bonds: tools
			.filter(isToolOfKind(ToolKind.Bond))
			.map((tool) => mapBondToRequest(tool, context)),
		commodities: tools
			.filter(isToolOfKind(ToolKind.Commodity))
			.map((tool) => mapCommodityToRequest(tool, context)),
		cryptocurrencies: tools
			.filter(isToolOfKind(ToolKind.Crypto))
			.map((tool) => mapCryptoToRequest(tool, context)),
		bankProducts: tools
			.filter(isToolOfKind(ToolKind.BankProduct))
			.map((tool) => mapBankProductToRequest(tool, context)),
	};
};
